import * as React from "react"
import { useEffect, useState } from "react"
import { Text, TextStyle, TouchableOpacity, View, ViewStyle } from "react-native"

import { Redirect, Stack, useGlobalSearchParams, usePathname } from "expo-router"
import { usePostHog } from "posthog-react-native"

import ActivityTrackerWrapper from "src/components/ActivityTrackerWrapper"
import { ChannelSplash } from "src/components/ChannelSplash"
import { Loading } from "src/components/Loading"
import Config from "src/config"
import { useAuth } from "src/context/AuthContext"
import { ChannelBrandProvider, useChannelBrand } from "src/context/ChannelBrandContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { ChatProvider } from "src/context/ChatContext"
import { LocationProvider } from "src/context/LocationContext"
import { MediaAvatarProvider } from "src/context/MediaAvatarContext"
import { MediaContextProvider } from "src/context/MediaContextProvider"
import { MediaPipProvider } from "src/context/MediaPipContext"
import { MicrophoneProvider } from "src/context/MicrophoneContext"
import { ResponseTimingProvider } from "src/context/ResponseTimingContext"
import { RoleProvider } from "src/context/RoleContext"
import { SentimentModalProvider } from "src/context/SentimentModalContext"
import { SidekickInteractionProvider } from "src/context/SidekickInteractionContext"
import { SidekickProgressProvider } from "src/context/SidekickProgressContext"
import { ThumbnailQueueProvider } from "src/context/ThumbnailQueueContext"
import { TypingProvider } from "src/context/TypingContext"
import { VisemeProvider } from "src/context/VisemeContext"
import { WebsocketProvider } from "src/context/WebsocketContext"
import { useAiProcessingNoticeAcceptance } from "src/hooks/useAiProcessingNoticeAcceptance"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useTermsAcceptance } from "src/hooks/useTermsAcceptance"
import { useBackendAPIVersion } from "src/queries/useApi"
import { useMemberFactFieldsQuery, useMskAvailableQuery } from "src/queries/useConfig"
import { useRoleInfoQuery } from "src/queries/useRole"
import { useToDoList } from "src/queries/useToDoList"
import { useLogoutStatus, useMember, useMemberQuery } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"

/**
 * Thin version-gate wrapper. Checks authentication and backend API compatibility
 * before mounting AuthenticatedLayout. Keeping queries out of this component
 * prevents them from firing in parallel with the version check, which would
 * cause 401-triggered logouts on a version mismatch.
 */
export default function Layout() {
  const { isAuthenticated } = useAuth()
  const { isLoggingOut } = useLogoutStatus()
  const { data: backendVersion, isPending: isVersionPending } = useBackendAPIVersion()
  const pathname = usePathname()
  const { channel: channelParam } = useGlobalSearchParams<{ channel?: string | string[] }>()
  const { pendingChannelId } = useChannelLink()

  // isAuthenticated flips false as soon as logout starts (via isLoggingOut), but the
  // server logout request is still in flight. Stay on the current screen until cleanup
  // finishes and useCleanupThenLogout navigates to the unauth destination.
  if (!isAuthenticated && isLoggingOut) {
    return <Loading message="Logging out…" />
  }

  if (!isAuthenticated) {
    // Honor channel deeplinks by routing unauthenticated visitors to the channel splash,
    // which then forwards to the public content library when `is_media_mode` is true.
    // Check the URL param first, then fall back to the stored pendingChannelId so that
    // mobile-web users whose deeplink URL loses the query param (e.g. after a browser
    // refresh or a failed native-app open) still land on the channel library rather
    // than the generic login screen.
    const channelIdFromParam = Array.isArray(channelParam) ? channelParam[0] : channelParam
    const channelIdForSplash = channelIdFromParam?.trim() || pendingChannelId?.trim()
    if (channelIdForSplash) {
      return <Redirect href={`/unauth/channel-splash/${channelIdForSplash}`} />
    }
    return <Redirect href="/unauth/login" />
  }

  if (isVersionPending) return <Loading message="Getting things ready…" />

  if (backendVersion !== undefined && backendVersion !== Config.BACKEND_API_VERSION) {
    // Pathname guard breaks the redirect loop: this layout stays mounted while
    // the outdated screen is active, so we render a minimal stack instead of
    // redirecting again.
    if (pathname === "/outdated") {
      return (
        <Stack screenOptions={{ headerShown: false }}>
          <Stack.Screen name="outdated" />
        </Stack>
      )
    }
    return <Redirect href="/outdated" />
  }

  return <AuthenticatedLayout />
}

/** Full authenticated app — only mounts after the version gate clears. */
function AuthenticatedLayout() {
  const { sponsorId, logout, clearAllTemporaryData, getUserEmail } = useAuth()
  const { themed } = useAppTheme()
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  // FIXME: these queries must be completed before we allow the ProviderStack to execute.
  // Several places in the Context stack require these values on first run but have no way
  // to prevent children execution or pause rendering. Likely should integrate loading certain
  // types of data with the navigator/router itself.
  const memberQuery = useMemberQuery()
  const memberFactFieldsQuery = useMemberFactFieldsQuery()
  const mskAvailableQuery = useMskAvailableQuery()
  const roleInfoQuery = useRoleInfoQuery()
  const posthog = usePostHog()
  // const { data: userId } = useUserId()
  // const { setAwaitingUserSentence } = useTyping()
  const member = useMember()
  const userEmail = getUserEmail()
  const { hasAcceptedTerms } = useTermsAcceptance(userEmail)
  const { hasAcceptedAiProcessingNotice } = useAiProcessingNoticeAcceptance(userEmail)
  // Warm the todos cache early to prevent settings menu flicker when hidden state for content library changes
  useToDoList()

  useEffect(() => {
    if (member) {
      const userId = member.user_id
      const isSme = member.sme
      const isSponsorAdmin = member.sponsor_admin
      posthog?.identify(userId, { sponsorId, isSponsorAdmin, isSme })
    }
  }, [posthog, member, sponsorId])

  // Check if user needs to select a valid sponsor before accessing the app
  if (!sponsorId || sponsorId === "unknown-sponsor") {
    return <Redirect href="/setup/select-sponsor" />
  }

  // If any required query has permanently failed, give the user a way to retry or escape
  const hasLoadingError =
    memberQuery?.isError ||
    mskAvailableQuery?.isError ||
    memberFactFieldsQuery?.isError ||
    roleInfoQuery?.isError

  const isRetrying =
    memberQuery?.isFetching ||
    mskAvailableQuery?.isFetching ||
    memberFactFieldsQuery?.isFetching ||
    roleInfoQuery?.isFetching
  const isBusy = isRetrying || isLoggingOut

  if (hasLoadingError) {
    const retryAll = () => {
      memberQuery.refetch()
      mskAvailableQuery.refetch()
      memberFactFieldsQuery.refetch()
      roleInfoQuery.refetch()
    }
    const handleLogout = async () => {
      setIsLoggingOut(true)
      try {
        await logout()
        clearAllTemporaryData()
      } finally {
        setIsLoggingOut(false)
      }
    }
    return (
      <View style={themed($errorContainer)}>
        <Text style={themed($errorMessage)}>Something went wrong while loading your account.</Text>
        <TouchableOpacity style={themed($errorButton)} onPress={retryAll} disabled={isBusy}>
          <Text style={themed($errorButtonText)}>{isRetrying ? "Retrying…" : "Retry"}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={themed($errorSecondaryButton)}
          onPress={handleLogout}
          disabled={isBusy}
        >
          <Text style={themed($errorSecondaryButtonText)}>
            {isLoggingOut ? "Logging out…" : "Log out"}
          </Text>
        </TouchableOpacity>
      </View>
    )
  }

  // Records we want loaded from remote before we begin trying to use the app
  if (
    !memberQuery?.isSuccess ||
    !mskAvailableQuery?.isSuccess ||
    !memberFactFieldsQuery?.isSuccess ||
    !roleInfoQuery?.isSuccess
  ) {
    return <Loading message="Getting things ready…" />
  }

  // Gate on legal acceptance only after queries prove the session is valid.
  // This prevents stale tokens (e.g. restored by Android Auto Backup after
  // uninstall/reinstall) from showing ToS/AI screens before the user has
  // actually logged in — the stale tokens will fail the queries above first,
  // forcing re-authentication.
  if (!hasAcceptedTerms) {
    return <Redirect href="/setup/accept-terms" />
  }

  if (!hasAcceptedAiProcessingNotice) {
    return <Redirect href="/setup/accept-ai-processing" />
  }

  return (
    <ProviderStack>
      <Stack
        initialRouteName="index"
        // ensures we never go to the modal screen on fast-refresh because we don't explicitly define (tabs)
      >
        <Stack.Screen name="index" options={{ headerShown: false }} />
        <Stack.Screen
          name="member-profile"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="llm-selection"
          options={{
            title: "LLM Selection",
            headerBackTitle: "Back",
          }}
        />
        <Stack.Screen
          name="diagnostics"
          options={{
            title: "Diagnostics",
            headerBackTitle: "Back",
          }}
        />
        <Stack.Screen
          name="youtube-player"
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name="feedback"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="help"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="outdated"
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen name="video-player" options={{ headerShown: false }} />
        <Stack.Screen name="audio-player" options={{ headerShown: false }} />
        <Stack.Screen name="web-browser" options={{ headerShown: false }} />
        <Stack.Screen name="file-viewer" options={{ headerShown: false }} />
        <Stack.Screen name="pdf-viewer" options={{ headerShown: false }} />
        <Stack.Screen name="image-viewer" options={{ headerShown: false }} />
        <Stack.Screen
          name="menu"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="content-library"
          options={{
            headerShown: false,
            animation: "none",
          }}
        />
        <Stack.Screen
          name="change-role"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="change-channel"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="select-msk"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="settings"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
        <Stack.Screen
          name="crisis-line"
          options={{
            headerShown: false,
            presentation: "fullScreenModal",
          }}
        />
      </Stack>
      {/* Global channel splash overlay. Rendered here (rather than inside a single
          screen) so it shows and dismisses on ANY entry point — direct loads, web
          refreshes, and deep links into /content-library or other routes — not just
          the root chat screen. If it only lived on index, those direct entries would
          leave shouldShowSplash stuck true forever, silently gating audio playback. */}
      <GlobalChannelSplash />
    </ProviderStack>
  )
}

/** Renders the channel splash overlay for all authenticated routes. */
const GlobalChannelSplash = () => {
  const { brandData, shouldShowSplash, markSplashShown } = useChannelBrand()
  if (!shouldShowSplash) return null
  return <ChannelSplash brandData={brandData} onDismiss={markSplashShown} />
}

// Put all the providers for AUTHED users here:
const ProviderStack = ({ children }: { children: React.ReactNode }) => {
  return (
    <ChannelBrandProvider>
      <WebsocketProvider>
        <ResponseTimingProvider>
          <RoleProvider>
            {/* RiveProvider lives in the root layout so unauth screens (channel-splash,
                channel-library, media-viewer) can render the avatar without auth. */}
            <MicrophoneProvider>
              <VisemeProvider>
                <TypingProvider>
                  {/* LocationProvider is consumed by ChatContext (refreshLocation),
                      so it must wrap ChatProvider below. */}
                  <LocationProvider>
                    <ActivityTrackerWrapper>
                      <SentimentModalProvider>
                        {/* MediaContextProvider tracks active media for context-aware responses */}
                        <MediaContextProvider>
                          {/* ThumbnailQueueProvider ensures video thumbnails generate one at a time to avoid blocking microphone */}
                          <ThumbnailQueueProvider>
                            {/* The ChatProvider needs access to all it's parent contexts */}
                            <ChatProvider>
                              {/* SidekickInteractionProvider consolidates all interaction state */}
                              <SidekickInteractionProvider>
                                {/* SidekickProgressProvider drives step-by-step progress UI via backend events (RAG/LLM/TTS) */}
                                <SidekickProgressProvider>
                                  {/* MediaAvatarProvider coordinates avatar sizing with media playback */}
                                  <MediaAvatarProvider>
                                    {/* MediaPipProvider (historical name) is now a thin
                                          typing-activation signal bus shared between ButtonCluster
                                          and TypingChatInputDock. Picture-in-Picture is gone. */}
                                    <MediaPipProvider>{children}</MediaPipProvider>
                                  </MediaAvatarProvider>
                                </SidekickProgressProvider>
                              </SidekickInteractionProvider>
                            </ChatProvider>
                          </ThumbnailQueueProvider>
                        </MediaContextProvider>
                      </SentimentModalProvider>
                    </ActivityTrackerWrapper>
                  </LocationProvider>
                </TypingProvider>
              </VisemeProvider>
            </MicrophoneProvider>
          </RoleProvider>
        </ResponseTimingProvider>
      </WebsocketProvider>
    </ChannelBrandProvider>
  )
}

const $errorContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  padding: 32,
  gap: 16,
  backgroundColor: colors.background,
})

const $errorMessage: ThemedStyle<TextStyle> = ({ colors }) => ({
  textAlign: "center",
  fontSize: 16,
  color: colors.text,
  marginBottom: 8,
})

const $errorButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.text,
  paddingVertical: 12,
  paddingHorizontal: 32,
  borderRadius: 8,
  width: "100%",
  alignItems: "center",
})

const $errorButtonText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.background,
  fontSize: 16,
  fontWeight: "600",
})

const $errorSecondaryButton: ThemedStyle<ViewStyle> = () => ({
  paddingVertical: 12,
  paddingHorizontal: 32,
  width: "100%",
  alignItems: "center",
})

const $errorSecondaryButtonText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.textDim,
  fontSize: 14,
})
